import React, { useState, useCallback } from 'react';
import { Form, Input, Button, Alert } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ShieldAlert, CheckCircle2, LogOut } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../variables/api/axiosInstance';
import { getUser, PASSWORD_STRENGTH_COLOR, setUser } from '../../variables/constants';
import { authService } from '../../services/authService';

// Règles de robustesse du mot de passe (libellé : security:changementObligatoire.regles.<id>)
const RULES = [
    { id: 'len', test: (v) => v.length >= 8 },
    { id: 'upper', test: (v) => /[A-Z]/.test(v) },
    { id: 'lower', test: (v) => /[a-z]/.test(v) },
    { id: 'digit', test: (v) => /[0-9]/.test(v) },
    { id: 'special', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const score = (pwd) => RULES.filter((r) => r.test(pwd)).length;

// Indicateur de force
function StrengthBar({ value }) {
    if (!value) return null;
    const s = score(value);
    const st = PASSWORD_STRENGTH_COLOR[s] || PASSWORD_STRENGTH_COLOR[0];
    return (
        <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                {RULES.map((_, i) => (
                    <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i < s ? st.color : '#e5e7eb',
                        transition: 'background 0.25s',
                    }} />
                ))}
            </div>
            <span style={{ fontSize: 11, color: st.color, fontWeight: 600 }}>{st.label}</span>
        </div>
    );
}

// Checklist des règles
function RuleList({ value }) {
    const { t } = useTranslation('security');
    return (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {RULES.map((r) => {
                const ok = value ? r.test(value) : false;
                return (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{
                            width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                            background: ok ? '#dcfce7' : '#f3f4f6',
                            border: `1.5px solid ${ok ? '#16a34a' : '#d1d5db'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                        }}>
                            {ok && (
                                <svg width="8" height="8" viewBox="0 0 10 10">
                                    <polyline points="1.5,5 4,7.5 8.5,2" fill="none" stroke="#16a34a"
                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </div>
                        <span style={{ fontSize: 12, color: ok ? '#166534' : '#6b7280' }}>{t(`changementObligatoire.regles.${r.id}`)}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Modale de blocage
export default function ForceChangePassword({ onSuccess }) {
    const { t } = useTranslation('security');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [error, setError] = useState('');
    const [pwd, setPwd] = useState('');
    const navigate = useNavigate();
    const user = getUser();

    // Cherche l'UUID dans tous les noms de champs possibles
    const trackingId =
        user?.utilisateurTrackingId;

    // Changement de mot de passe
    const handleSubmit = useCallback(async ({ currentPassword, newPassword }) => {
        setError('');
        setLoading(true);

        if (!trackingId) {
            setError(t('changementObligatoire.messages.utilisateurInconnu'));
            setLoading(false);
            return;
        }

        try {
            // ChangePasswordRequest attend ancienMotDePasse / nouveauMotDePasse (@NotBlank).
            await axiosInstance.put(`/v1/utilisateurs/${trackingId}/changer-password`, {
                ancienMotDePasse: currentPassword,
                nouveauMotDePasse: newPassword,
            });
            const updated = { ...user, mustChangePassword: false };
            setUser(updated);
            toast.success(t('changementObligatoire.messages.misAJour'));
            onSuccess?.();
        } catch (err) {
            const msg = err?.response?.data?.message || t('changementObligatoire.messages.erreur');
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [user, onSuccess, t]);

    //Déconnexion
    const handleLogout = useCallback(async () => {
        setLogoutLoading(true);
        try {
            await authService.logout();
        } finally {
            setLogoutLoading(false);
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    const isStrong = score(pwd) >= 4;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15, 31, 23, 0.72)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
        }}>
            <div style={{
                background: '#fff',
                borderRadius: 20,
                width: '100%', maxWidth: 460,
                boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
                overflow: 'hidden',
                animation: 'cpSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
                <style>{`
                    @keyframes cpSlideIn {
                        from { opacity: 0; transform: translateY(24px) scale(0.97); }
                        to   { opacity: 1; transform: none; }
                    }
                `}</style>

                {/* ── En-tête ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #163d28 0%, #1a6b3c 100%)',
                    padding: '28px 32px 24px',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14,
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <div style={{
                            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
                            background: 'rgba(213,201,59,0.18)',
                            border: '1.5px solid rgba(213,201,59,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <ShieldAlert size={22} color="#d5c93b" strokeWidth={1.8} />
                        </div>
                        <div>
                            <div style={{ color: '#d5c93b', fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>
                                {t('changementObligatoire.surTitre')}
                            </div>
                            <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
                                {t('changementObligatoire.titre')}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                                {t('changementObligatoire.explication')}
                            </div>
                        </div>
                    </div>

                    {/* Bouton déconnexion en haut à droite */}
                    <button
                        onClick={handleLogout}
                        disabled={logoutLoading}
                        title={t('changementObligatoire.seDeconnecter')}
                        style={{
                            flexShrink: 0,
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: 8,
                            padding: '6px 10px',
                            cursor: logoutLoading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: 12, fontWeight: 500,
                            transition: 'background 0.15s',
                            opacity: logoutLoading ? 0.6 : 1,
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                    >
                        <LogOut size={14} strokeWidth={2} />
                        {logoutLoading ? t('changementObligatoire.deconnexionEnCours') : t('changementObligatoire.seDeconnecter')}
                    </button>
                </div>

                {/* Formulaire */}
                <div style={{ padding: '24px 32px 28px' }}>
                    {error && (
                        <Alert
                            type="error" message={error} showIcon
                            style={{ marginBottom: 18, borderRadius: 8 }}
                            closable onClose={() => setError('')}
                        />
                    )}

                    <Form form={form} layout="vertical" onFinish={handleSubmit}>

                        <Form.Item
                            label={<span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('changementObligatoire.champs.motDePasseActuel')}</span>}
                            name="currentPassword"
                            rules={[{ required: true, message: t('changementObligatoire.champs.saisirMotDePasseActuel') }]}
                            style={{ marginBottom: 16 }}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                                placeholder="••••••••"
                                iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                style={{ borderRadius: 10, height: 44 }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('changementObligatoire.champs.nouveauMotDePasse')}</span>}
                            name="newPassword"
                            rules={[
                                { required: true, message: t('changementObligatoire.champs.choisirNouveauMotDePasse') },
                                {
                                    validator: (_, value) =>
                                        !value || score(value) >= 4
                                            ? Promise.resolve()
                                            : Promise.reject(new Error(t('changementObligatoire.champs.criteresNonRespectes'))),
                                },
                            ]}
                            style={{ marginBottom: 4 }}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={t('changementObligatoire.champs.placeholderNouveau')}
                                iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                style={{ borderRadius: 10, height: 44 }}
                                onChange={(e) => setPwd(e.target.value)}
                            />
                        </Form.Item>

                        <StrengthBar value={pwd} />
                        <RuleList value={pwd} />

                        <Form.Item
                            label={<span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('changementObligatoire.champs.confirmer')}</span>}
                            name="confirmPassword"
                            dependencies={['newPassword']}
                            style={{ marginTop: 18, marginBottom: 20 }}
                            rules={[
                                { required: true, message: t('changementObligatoire.champs.confirmerObligatoire') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                        return Promise.reject(new Error(t('changementObligatoire.champs.nonIdentiques')));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                                placeholder="••••••••"
                                iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                style={{ borderRadius: 10, height: 44 }}
                            />
                        </Form.Item>

                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={!isStrong}
                            style={{
                                width: '100%', height: 48, borderRadius: 10,
                                fontSize: 14, fontWeight: 600,
                                background: isStrong ? '#1a6b3c' : '#9ca3af',
                                border: 'none',
                                transition: 'background 0.2s',
                            }}
                        >
                            {loading ? t('changementObligatoire.enregistrement') : t('changementObligatoire.definir')}
                        </Button>
                    </Form>

                    <div style={{
                        marginTop: 16, padding: '10px 14px', borderRadius: 8,
                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <CheckCircle2 size={14} color="#16a34a" strokeWidth={2} />
                        <span style={{ fontSize: 12, color: '#166534', lineHeight: 1.4 }}>
                            {t('changementObligatoire.redirection')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}